import mapParcelOrg from 'dataMappers/parcelOrg.dataMapper';
import PARCEL_STAGE from '@pperform/pp-constants/parcelStage.constants';
import {
  RETURN_REQUEST_LEVEL,
  SEARCH_FIELD_KEYS,
} from '@pperform/pp-constants/widgetConfigs';
import { ReturnTicket } from '@pperform/pp-data-mappers';
import { Scenario } from 'constants/scenario.constants';
import { ShipmentType } from 'constants/shipmentTypes.constants';
import APIError from 'exceptions/APIError';
import AppError from 'exceptions/AppError';
import { fetchShipments } from 'fetchers/shipment.fetchers';
import {
  FeatureToggles,
  PhaseEventsVisibility,
} from 'interfaces/FeatureToggle.interface';
import type { GlobalConfig } from 'interfaces/GlobalConfig.interface';
import type { ShipmentParams } from 'interfaces/Params.interface';
import type Shipment from 'interfaces/PmParcel';
import { getScenarioParams } from 'services/globalConfig.services';
import { isRuntimeError } from 'utils/fetcher.utils';
import {
  applyPickupLocationToParcelEvents,
  applyPickupLocationToMixedEvents,
} from 'utils/pickupLocation.utils';
import { fetchReturnTickets } from './returnTicket.service';
import { ptpCatchError } from './sentry.services/sentry.services';
import { userAuthService } from './userAuth.services';

const IGNORE_EVENT_PHASE_A_KEY = ['A10', 'A40', 'A60'] as const;

const PHASE_EVENTS = { PHASE_A: 'A', PHASE_B: 'B', PHASE_C: 'C' } as const;

/**
 * Helper: Process preprinted return shipment (with parcel.events)
 */
const processPreprrintedReturnShipment = (
  shipment: Shipment,
  pickupPoint: string,
): Shipment => {
  // Used in Tracking Timeline
  const modifiedParcelEvents = applyPickupLocationToParcelEvents(
    shipment.parcel?.events,
    pickupPoint,
  );

  // Used in Parcel Details, mixedEvents[0]
  const modifiedMixedEvents = applyPickupLocationToMixedEvents(
    shipment.mixedEvents,
    pickupPoint,
  );

  return {
    ...shipment,
    ...(shipment.parcel && {
      parcel: {
        ...shipment.parcel,
        events: modifiedParcelEvents,
      },
    }),
    mixedEvents: modifiedMixedEvents,
    latestMixedEvent: modifiedMixedEvents?.[0],
  };
};

/**
 * Helper: Process non-preprinted return shipment (with mixedEvents)
 */
const processNonPreprintedReturnShipment = (
  shipment: Shipment,
  pickupPoint: string,
): Shipment => {
  // Used in both Parcel Details and Tracking Timeline
  const modifiedMixedEvents = applyPickupLocationToMixedEvents(
    shipment.mixedEvents,
    pickupPoint,
  );

  return {
    ...shipment,
    mixedEvents: modifiedMixedEvents,
    latestMixedEvent: modifiedMixedEvents?.[0],
  };
};

/**
 * Helper: Process forward shipment (with parcel.events and shipment.events)
 */
const processForwardShipment = (
  shipment: Shipment,
  pickupPoint: string,
): Shipment => {
  // Used in Parcel Details, events[0]
  const modifiedShipmentEvents = applyPickupLocationToParcelEvents(
    shipment.events,
    pickupPoint,
  );

  // Used in Tracking Timeline
  const modifiedParcelEvents = applyPickupLocationToParcelEvents(
    shipment.parcel?.events,
    pickupPoint,
  );

  return {
    ...shipment,
    events: modifiedShipmentEvents,
    ...(shipment.parcel && {
      parcel: {
        ...shipment.parcel,
        events: modifiedParcelEvents,
      },
    }),
  };
};

/**
 * Processes shipments to replace event locations with pickup_point value
 * from the latest events back to and including G32 event (Arrived at parcel store)
 *
 * TODO: Review and clarify event array handling for preprinted returns:
 * - MainInfo.tsx switches between parcel.events and mixedEvents based on enablePreprintedReturnLabel
 * - ShipmentDetails.tsx always uses latestMixedEvent for both scenarios
 * - Current implementation updates all arrays for consistency, but may need refactoring
 * - Consider if latestMixedEvent should be derived from parcel.events for preprinted scenario
 */
export const processShipmentsPickupLocation = (
  shipments: Shipment[],
  featureToggles?: FeatureToggles,
): Shipment[] => {
  return shipments.map((shipment) => {
    // Get pickup_point from custom fields
    const pickupPoint = shipment.customFields?.find(
      ({ key }) => key === 'pickup_point',
    )?.value;

    // If no pickup_point, return shipment unchanged
    if (!pickupPoint) {
      return shipment;
    }

    const enablePreprintedReturnLabel =
      featureToggles?.returnManagement?.enablePreprintedReturnLabel;

    // Route to appropriate processor based on shipment type and feature flags
    if (shipment.typeOfShipment === ShipmentType.Return) {
      return enablePreprintedReturnLabel
        ? processPreprrintedReturnShipment(shipment, pickupPoint)
        : processNonPreprintedReturnShipment(shipment, pickupPoint);
    }

    return processForwardShipment(shipment, pickupPoint);
  });
};

const sortShipmentByType = (shipments: Shipment[], scenario: Scenario) => {
  const returnShipment = shipments.filter(
    (shipment) => shipment.typeOfShipment === ShipmentType.Return,
  );

  const forwardShipment = shipments.filter(
    (shipment) => !shipment.typeOfShipment,
  );

  // Scenario is return, priority return shipment to top
  if (scenario === Scenario.Return) {
    return [...returnShipment, ...forwardShipment];
  }

  // priority forward shipment to top
  return [...forwardShipment, ...returnShipment];
};

const getReturnTickets = async (
  organizationId: number,
  shipments: Shipment[],
  returnLevel: RETURN_REQUEST_LEVEL,
) => {
  let res;
  if (returnLevel === RETURN_REQUEST_LEVEL.SHIPMENT) {
    const returnPks = shipments
      .filter((shipment) => shipment.typeOfShipment === ShipmentType.Return)
      .map((shipment) => shipment.id);

    const forwardPks = shipments
      .filter((shipment) => shipment.typeOfShipment !== ShipmentType.Return)
      .map((shipment) => shipment.id);

    try {
      res = await fetchReturnTickets(organizationId, {
        shipmentPks: returnPks,
        forwardShipmentPks: forwardPks,
      });
    } catch (error) {
      if (isRuntimeError(error)) {
        ptpCatchError(error);
      }
    }
  } else if (returnLevel === RETURN_REQUEST_LEVEL.ORDER) {
    const [firstShipment] = shipments;
    try {
      res = await fetchReturnTickets(organizationId, {
        orderIds: [firstShipment.orderId],
      });
    } catch (error) {
      if (isRuntimeError(error)) {
        ptpCatchError(error);
      }
    }
  }

  return res;
};

const shouldFetchReturnTickets = (
  shipment: Shipment,
  featureToggles: FeatureToggles,
): boolean => {
  const isReturn = shipment.typeOfShipment === ShipmentType.Return;
  const isOutbound = !shipment.typeOfShipment;

  if (isReturn) {
    // For return shipments, check if preprinted label feature toggle is OFF
    const isPreprintedEnabled =
      featureToggles?.returnManagement?.enablePreprintedReturnLabel;
    return !isPreprintedEnabled;
  }

  if (isOutbound) {
    const { lastEvent } = shipment.parcel;
    if (!lastEvent) return false;

    // For outbound shipments, check if it's delivered in general (collection point, return to sender, delivered, etc.)
    const { eventTypeMasterData } = lastEvent;
    return eventTypeMasterData?.stageKey === PARCEL_STAGE.delivered;
  }

  return false;
};

const processShipmentReturnTickets = async (
  organizationId: number,
  shipments: Shipment[],
  returnLevel: RETURN_REQUEST_LEVEL,
  featureToggles: FeatureToggles,
) => {
  // Split shipments into two arrays based on whether they need return tickets
  const shipmentsNeedingReturnTickets = shipments.filter((shipment) =>
    shouldFetchReturnTickets(shipment, featureToggles),
  );
  const shipmentsNotNeedingReturnTickets = shipments.filter(
    (shipment) => !shouldFetchReturnTickets(shipment, featureToggles),
  );

  // If no shipments need return tickets, return original shipments unchanged
  if (shipmentsNeedingReturnTickets.length === 0) {
    return shipments;
  }

  const returnTickets = await getReturnTickets(
    organizationId,
    shipmentsNeedingReturnTickets,
    returnLevel,
  );

  const processShipments = (shipment: Shipment) => {
    let returnTicketData;

    // Determine which return tickets to use based on return level
    if (returnLevel === RETURN_REQUEST_LEVEL.SHIPMENT) {
      returnTicketData =
        shipment.typeOfShipment === ShipmentType.Return
          ? returnTickets.shipmentPks[shipment.id]
          : returnTickets.forwardShipmentPks[shipment.id];
    } else {
      returnTicketData = returnTickets.orderIds[shipment.orderId];
    }

    // Process return ticket data
    return {
      ...shipment,
      returnTickets: returnTicketData?.map((ticket) =>
        ReturnTicket.fromApiData(ticket),
      ),
      returnTicket: ReturnTicket.fromApiData(returnTicketData?.[0]),
    };
  };

  // Shipment with return tickets
  const processedShipments =
    shipmentsNeedingReturnTickets.map(processShipments);

  return [...processedShipments, ...shipmentsNotNeedingReturnTickets];
};

const processShipmentsEventsVisibility = (
  shipments: Shipment[],
  featureToggles: FeatureToggles,
): Shipment[] => {
  const {
    phaseAEventsVisibility,
    phaseBEventsVisibility,
    phaseCEventsVisibility,
  } = featureToggles?.ptpEventVisibility || {};

  if (
    phaseAEventsVisibility === PhaseEventsVisibility.DISPLAY &&
    phaseBEventsVisibility === PhaseEventsVisibility.DISPLAY &&
    phaseCEventsVisibility === PhaseEventsVisibility.DISPLAY
  ) {
    return shipments;
  }

  const filterEvents = (events?: any[]) => {
    if (!events?.length) return [];

    return events.filter((event) => {
      if (
        phaseAEventsVisibility === PhaseEventsVisibility.HIDE &&
        event?.eventTypeMasterData?.phaseKey === PHASE_EVENTS.PHASE_A &&
        !IGNORE_EVENT_PHASE_A_KEY.includes(event?.eventTypeMasterData?.key)
      ) {
        return false;
      }

      if (
        phaseBEventsVisibility === PhaseEventsVisibility.HIDE &&
        event?.eventTypeMasterData?.phaseKey === PHASE_EVENTS.PHASE_B
      ) {
        return false;
      }

      if (
        phaseCEventsVisibility === PhaseEventsVisibility.HIDE &&
        event?.eventTypeMasterData?.phaseKey === PHASE_EVENTS.PHASE_C
      ) {
        return false;
      }

      return true;
    });
  };

  // TODO: Update logic to add parcel.events for Preprinted Return Shipment UC
  // Currently, parcel.events of Preprinted only shows from Phase D so code still OK because we don't have that visibility yet
  return shipments.map((shipment) => {
    const shipmentEvents = {
      events: filterEvents(shipment.events),
      mixedEvents: filterEvents(shipment.mixedEvents),
      ...(shipment.parcel && {
        parcel: {
          ...shipment.parcel,
          events: filterEvents(shipment.parcel.events),
        },
      }),
    };

    return {
      ...shipment,
      ...shipmentEvents,
      latestMixedEvent: shipmentEvents.mixedEvents[0],
      isNoInfoAvailable: shipmentEvents.events.length === 0,
    };
  });
};

const getLinkedShipmentFromSearchTerm = (
  globalConfig: GlobalConfig,
  searchTerm: ShipmentParams[],
): string => {
  const linkShipmentField = globalConfig.config.searchingIdReferencesBox.find(
    (field) => field.key === SEARCH_FIELD_KEYS.LINKED_SHIPMENT_TRACKING_NUMBER,
  );
  if (linkShipmentField) {
    const linkShipmentTerm = searchTerm.find(
      (term) =>
        term.key === linkShipmentField.urlParam ||
        term.key === linkShipmentField.tagParam,
    );
    return linkShipmentTerm?.value;
  }
  return null;
};

export const getShipments = async (
  globalConfig: GlobalConfig,
  searchTerms: ShipmentParams[],
  featureToggles?: FeatureToggles,
): Promise<Shipment[]> => {
  const { organizationId, config, cmsSlug, organizationSlug } = globalConfig;
  const {
    language,
    return: {
      returnRequestScope: { returnLevel },
    },
  } = config;
  const scenario = getScenarioParams();

  try {
    const {
      data: { shipments: shipmentsDTO, token },
    } = await fetchShipments({
      cmsSlug,
      language,
      searchTerms,
      organizationId,
      scenario,
      organizationSlug,
    });
    userAuthService.setValidateUserToken(token);

    const linkedShipmentTrackingNumber = getLinkedShipmentFromSearchTerm(
      globalConfig,
      searchTerms,
    );

    let shipments: Shipment[] = shipmentsDTO.map((item) => {
      const s = mapParcelOrg(item);
      s.additionalData.linkedShipmentTrackingNumber =
        s.additionalData?.linkedShipmentTrackingNumber ||
        linkedShipmentTrackingNumber;

      return s;
    });

    shipments = await processShipmentReturnTickets(
      organizationId,
      shipments,
      returnLevel,
      featureToggles,
    );

    shipments = processShipmentsEventsVisibility(shipments, featureToggles);

    // Only process pickup location if PTP Config "Display pick-up location information" is YES
    if (config.parcelDetailsBox.shouldShowPickupLocation) {
      shipments = processShipmentsPickupLocation(shipments, featureToggles);
    }

    shipments = sortShipmentByType(shipments, scenario);

    return shipments;
  } catch (error) {
    if (error instanceof APIError) {
      throw new AppError(error.message);
    }
    throw error;
  }
};
